import { useMemo, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { updateScholarshipPlayer } from '../../../../services/scholarships';
import type { ScholarshipPlayer } from '../../../../types/scholarships';
import { getSafeErrorMessage } from '../../../../utils/errorHandler';
import { useTour } from '../../../tours/useTour';
import type { ScholarshipDetailContext } from './ScholarshipDetailLayout';
import { PitchingStaffTable } from './PitchingStaffTable';
import { PitchingRoleSummary } from './PitchingRoleSummary';
import { PitchTypeTotals } from './PitchTypeTotals';
import { RosterLegend } from './RosterLegend';
import { ScholarshipPlayerModal } from './ScholarshipPlayerModal';
import { ProspectQuickViewPanel } from '../players/ProspectQuickViewPanel';
import { openableProspectId } from './openableProspect';
import { toPlayerWriteRequest } from './playerWriteRequest';
import { rosterSettingsOf, type PitcherHand } from './rosterPalette';
import { pitchingStaffByHand } from './pitchingStaff';
import { useRosterPlayers } from './useRosterPlayers';
import { useRosterSport } from './useRosterSport';
import { configureBoardLabel, isRecruitingBoard } from './boardPurpose';
import './rosterManagement.css';

const HANDS: readonly PitcherHand[] = ['LHP', 'RHP'];

/**
 * The staff by hand: every row whose position is LHP or RHP, in the board's order for that
 * position, then the two-way players whose secondary position is that hand (a SS who also
 * pitches). Those carry no order at the hand, so they sit below the staff and do not drag.
 */
export const ScholarshipPitchingStaffTab = () => {
  const { scholarship, refresh: refreshScholarship } =
    useOutletContext<ScholarshipDetailContext>();
  const sport = useRosterSport();
  const settings = rosterSettingsOf(scholarship);
  const { players, error, setError, refresh, setPlayers, reorder, mergeSaved, trackWrite } =
    useRosterPlayers(scholarship.id);
  const [savingIds, setSavingIds] = useState<ReadonlySet<string>>(new Set());
  const [modalPlayer, setModalPlayer] = useState<ScholarshipPlayer | null>(null);
  /** The attached pitcher whose profile is open in the side panel. */
  const [viewProspectId, setViewProspectId] = useState<string | null>(null);
  // An attached pitcher opens their profile over the tables; a pitcher added by hand has none,
  // so it opens the roster edit form as before.
  const openPlayer = (pitcher: ScholarshipPlayer) => {
    const prospectId = openableProspectId(pitcher);
    if (prospectId) setViewProspectId(prospectId);
    else setModalPlayer(pitcher);
  };

  const byHand = useMemo(() => pitchingStaffByHand(players ?? []), [players]);
  const staff = useMemo(
    () => HANDS.flatMap((hand) => [...byHand[hand].primary, ...byHand[hand].twoWay]),
    [byHand],
  );

  const markSaving = (id: string, saving: boolean) =>
    setSavingIds((prev) => {
      const next = new Set(prev);
      if (saving) next.add(id);
      else next.delete(id);
      return next;
    });

  /*
    Saves through the player PUT, which is a full replace: the body is the row as it stands with
    only the role changed, so nothing else on the player is touched. Optimistic, with the rollback
    scoped to this row's role, because other rows may be mid-save or mid-drag at the same time.
    The select is disabled while its own save is in flight, so one row never has two saves racing.
  */
  const handleRoleChange = async (pitcher: ScholarshipPlayer, role: string | null) => {
    const previous = pitcher.pitchingRole ?? null;
    if (previous === role) return;
    const patchRole = (value: string | null) =>
      setPlayers((current) =>
        (current ?? []).map((p) => (p.id === pitcher.id ? { ...p, pitchingRole: value } : p)),
      );
    // Tracked, so a refetch already running (after a dialog save, say) cannot land after this
    // and put the old role back.
    await trackWrite(async () => {
      patchRole(role);
      markSaving(pitcher.id, true);
      setError(null);
      try {
        const updated = await updateScholarshipPlayer(
          scholarship.id,
          pitcher.id,
          toPlayerWriteRequest(pitcher, { pitchingRole: role }),
        );
        // Keep the local sort order: a drag may have landed while this save was in flight.
        setPlayers((current) =>
          (current ?? []).map((p) =>
            p.id === updated.id ? { ...updated, sortOrder: p.sortOrder } : p,
          ),
        );
      } catch (e) {
        patchRole(previous);
        setError(getSafeErrorMessage(e, 'Failed to save pitching role'));
      } finally {
        markSaving(pitcher.id, false);
      }
    });
  };

  useTour('rosters', {
    ready: players != null || error != null,
    hasData: staff.length > 0,
    pageTourKey: 'rosters.pitching',
    dataTourKey: null,
  });

  if (players == null && !error) {
    return <div>Loading pitching staff…</div>;
  }

  // The first table with anyone in it carries the row anchors.
  const anchorHand = HANDS.find(
    (hand) => byHand[hand].primary.length + byHand[hand].twoWay.length > 0,
  );

  return (
    <div className="roster-view pitching-staff-tab">
      {error && (
        <div className="roster-view__error" role="alert">
          {error}
        </div>
      )}

      <div className="pitching-staff-tab__tables">
        {HANDS.map((hand) => (
          <PitchingStaffTable
            key={hand}
            hand={hand}
            pitchers={byHand[hand].primary}
            twoWay={byHand[hand].twoWay}
            settings={settings}
            sport={sport}
            savingIds={savingIds}
            onReorder={reorder}
            onRoleChange={handleRoleChange}
            onOpenPlayer={openPlayer}
            ownsAnchors={hand === anchorHand}
          />
        ))}
      </div>

      <PitchingRoleSummary staff={staff} settings={settings} configureLabel={configureBoardLabel(scholarship)} />
      <PitchTypeTotals staff={staff} settings={settings} configureLabel={configureBoardLabel(scholarship)} />
      <RosterLegend settings={settings} sections={['handedness', 'indicators']} />

      {modalPlayer && (
        <ScholarshipPlayerModal
          sport={sport}
          scholarshipId={scholarship.id}
          scholarshipModel={scholarship.scholarshipModel}
          recruitingBoard={isRecruitingBoard(scholarship)}
          rosterSettings={settings}
          mode="edit"
          existingPlayer={modalPlayer}
          onClose={() => setModalPlayer(null)}
          onSuccess={(_cost, saved) => {
            setModalPlayer(null);
            // In place first: a role picked before the refetch lands is a full replace built from
            // this row, so it must already carry what the modal just saved (velo, shapes...).
            mergeSaved(saved);
            refresh();
            refreshScholarship();
          }}
          onProspectChanged={() => {
            refresh();
            refreshScholarship();
          }}
        />
      )}

      <ProspectQuickViewPanel
        prospectId={viewProspectId}
        isOpen={viewProspectId !== null}
        onClose={() => setViewProspectId(null)}
        onProspectChanged={() => {
          refresh();
          refreshScholarship();
        }}
      />
    </div>
  );
};
